import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Fade,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { sendMessage as sendMessageAPI } from "../api";
import AppointmentForm from "./modals/AppointmentForm";

const botAvatar = "/avatars/bot-neutral.png";
const userAvatar =
  "https://ui-avatars.com/api/?name=Guest&background=60a5fa&color=fff&rounded=true";

const SERVICE_CHOICES = [
  { key: "website", labelKey: "service_website", icon: "🌐" },
  { key: "webapp", labelKey: "service_webapp", icon: "🖥️" },
  { key: "mobile", labelKey: "service_mobile", icon: "📱" },
  { key: "talk", labelKey: "service_talk", icon: "💬" },
];

function getOrCreateSessionId() {
  let sessionId = localStorage.getItem("chat_session_id");
  if (!sessionId) {
    sessionId = Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem("chat_session_id", sessionId);
  }
  return sessionId;
}

function getUiLang(i18n) {
  const l = (i18n?.resolvedLanguage || i18n?.language || "el");
  return String(l).split("-")[0].toLowerCase();
}

export default function ChatWidget() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);

  const [showServices, setShowServices] = useState(false);
  const [awaitingConsent, setAwaitingConsent] = useState(false);
  const [consentShown, setConsentShown] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);

  const messagesEndRef = useRef(null);

  // ------- PREMIUM TUNING (radius / spacing)
  const PANEL_RADIUS = 20;
  const INPUT_RADIUS = 14;

  // ✅ dynamic navbar height -> CSS var
  useEffect(() => {
    const setTopOffset = () => {
      const header =
        document.querySelector("header") ||
        document.querySelector(".MuiAppBar-root") ||
        document.querySelector("nav");

      const headerH = header?.getBoundingClientRect?.().height || 0;
      const base = 10;
      const extra = Math.min(Math.max(headerH, 0), 120);
      document.documentElement.style.setProperty(
        "--chat-top-offset",
        `${base + extra}px`
      );
    };

    setTopOffset();
    window.addEventListener("resize", setTopOffset);
    window.addEventListener("orientationchange", setTopOffset);
    return () => {
      window.removeEventListener("resize", setTopOffset);
      window.removeEventListener("orientationchange", setTopOffset);
    };
  }, []);

  // Scroll to bottom
  useEffect(() => {
    if (!open) return;
    // requestAnimationFrame helps on mobile layout reflow
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [messages, open]);

  const buildServiceChoices = () =>
    SERVICE_CHOICES.map(({ key, labelKey, icon }) => ({
      key,
      label: `${icon} ${t(labelKey) || key}`,
    }));

  const pushWelcome = () => {
    setMessages([
      { sender: "bot", text: t("welcome") || "Welcome!", isChoice: false },
      {
        sender: "bot",
        text: t("choose_service") || "Choose a service:",
        isChoice: true,
        type: "service",
        choices: buildServiceChoices(),
      },
    ]);
    setShowServices(true);
  };

  // open -> reset flow
  useEffect(() => {
    if (!open) return;
    pushWelcome();
    setAwaitingConsent(false);
    setConsentShown(false);
    setShowAppointmentForm(false);
    setInput("");
    setThinking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, i18n.language]);

  const maybeShowConsent = (reply) => {
    const lower = (reply || "").toLowerCase();
    const isTrigger =
      (reply || "").includes("συμπληρώστε") ||
      lower.includes("fill in your details");

    if (!isTrigger || consentShown) return;

    setConsentShown(true);
    setAwaitingConsent(true);
    setMessages((msgs) => [
      ...msgs,
      {
        sender: "bot",
        text:
          t("do_you_want_to_leave_contact") ||
          "Would you like to leave your contact details?",
        isConsent: true,
      },
    ]);
  };

  const sendMessage = async (text) => {
    const clean = (text || "").trim();
    if (!clean) return;

    setThinking(true);
    setMessages((msgs) => [...msgs, { sender: "user", text: clean }]);
    setInput("");

    try {
      const lang = getUiLang(i18n);
      const data = await sendMessageAPI({
        text: clean,
        session_id: getOrCreateSessionId(),
        lang,
      });

      const reply = data?.reply || t("default_reply") || "Ok.";
      setMessages((msgs) => [...msgs, { sender: "bot", text: reply }]);
      maybeShowConsent(reply);
    } catch (e) {
      setMessages((msgs) => [
        ...msgs,
        { sender: "bot", text: t("error_message") || "Something went wrong." },
      ]);
    }

    setThinking(false);
  };

  const handleServiceSelect = async (serviceKey) => {
    const userText = t(`service_${serviceKey}`) || serviceKey;

    setShowServices(false);
    setThinking(true);
    setMessages((msgs) => [...msgs, { sender: "user", text: userText }]);

    try {
      const lang = getUiLang(i18n);
      const data = await sendMessageAPI({
        text: userText,
        session_id: getOrCreateSessionId(),
        lang,
        service_key: serviceKey,
      });


      const reply = data?.reply || t("default_reply") || "Ok.";
      setMessages((msgs) => [...msgs, { sender: "bot", text: reply }]);
      maybeShowConsent(reply);
    } catch (e) {
      setMessages((msgs) => [
        ...msgs,
        { sender: "bot", text: t("error_message") || "Something went wrong." },
      ]);
    }

    setThinking(false);
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (awaitingConsent) return;
    if (input.trim()) sendMessage(input.trim());
  };

  const handleConsentNo = () => {
    // ✅ reset consent state so it can re-trigger later properly
    setAwaitingConsent(false);
    setConsentShown(false);
    setShowAppointmentForm(false);

    setMessages((msgs) => [
      ...msgs,
      {
        sender: "bot",
        text:
          t("no_worries") ||
          "No worries — choose a service to continue.",
      },
      {
        sender: "bot",
        text: t("choose_service") || "Choose a service:",
        isChoice: true,
        type: "service",
        choices: buildServiceChoices(),
      },
    ]);

    setShowServices(true);
  };

  const Bubble = ({ isUser, children }) => (
    <Paper
      elevation={0}
      sx={{
        px: isMobile ? 1.05 : 1.5,
        py: isMobile ? 0.8 : 1.05,
        maxWidth: isMobile ? "82%" : "74%",
        bgcolor: isUser ? "rgba(59,130,246,0.96)" : "rgba(241,245,249,0.98)",
        color: isUser ? "#fff" : "#0f172a",
        borderRadius: 3,
        borderBottomRightRadius: isUser ? 8 : 14,
        borderBottomLeftRadius: isUser ? 14 : 8,
        border: isUser
          ? "1px solid rgba(255,255,255,0.14)"
          : "1px solid rgba(148,163,184,0.22)",
        boxShadow: isUser
          ? "0 12px 26px -18px rgba(56,189,248,.75)"
          : "0 10px 22px -18px rgba(148,163,184,.5)",
        backdropFilter: "blur(6px)",
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: isMobile ? "0.97rem" : "1.03rem",
          lineHeight: 1.35,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {children}
      </Typography>
    </Paper>
  );

  const ServiceGrid = ({ choices }) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1.1,
        mt: 0.9,
      }}
    >
      {choices.map(({ key, label }) => {
        const first = label.split(" ")[0];
        const rest = label.replace(first + " ", "");
        return (
          <Button
            key={key}
            onClick={() => handleServiceSelect(key)}
            variant="contained"
            sx={{
              borderRadius: 14,
              py: 1.05,
              px: 1.15,
              fontWeight: 950,
              textTransform: "none",
              justifyContent: "flex-start",
              gap: 1,
              background:
                "linear-gradient(135deg, rgba(30,41,59,1) 0%, rgba(51,65,85,1) 55%, rgba(15,23,42,1) 110%)",
              boxShadow: "0 18px 36px -28px rgba(15,23,42,.85)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(51,65,85,1) 0%, rgba(15,23,42,1) 100%)",
                transform: "translateY(-1px)",
              },
              transition: "transform .18s ease",
            }}
          >
            <span style={{ fontSize: "1.1rem" }}>{first}</span>
            <span style={{ fontSize: "0.98rem" }}>{rest}</span>
          </Button>
        );
      })}
    </Box>
  );

  const renderMessages = () =>
    messages.map((msg, idx) => {
      if (msg.isChoice && msg.type === "service") {
        return (
          <Box key={idx} sx={{ textAlign: "center", pt: 0.15 }}>
            <Typography
              sx={{
                mb: 0.75,
                fontWeight: 950,
                color: "#0f172a",
                fontSize: isMobile ? "1.03rem" : "1.08rem",
              }}
            >
              {msg.text}
            </Typography>
            <ServiceGrid choices={msg.choices} />
          </Box>
        );
      }

      if (msg.isConsent) {
        return (
          <Box key={idx} sx={{ textAlign: "center", mt: 0.4, mb: 0.8 }}>
            <Typography sx={{ mb: 0.95, color: "#0f172a", fontWeight: 900 }}>
              {msg.text}
            </Typography>

            <Stack direction="row" spacing={1.2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => {
                  setAwaitingConsent(false);
                  setShowAppointmentForm(true);
                  setMessages((msgs) => [
                    ...msgs,
                    {
                      sender: "bot",
                      text:
                        t("appointment_lets_fill_form") ||
                        "Please fill in the form and we will contact you.",
                    },
                  ]);
                }}
                sx={{
                  borderRadius: 12,
                  px: 3,
                  fontWeight: 950,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, rgba(30,41,59,1) 0%, rgba(51,65,85,1) 100%)",
                }}
              >
                {t("yes") || "Yes"}
              </Button>

              <Button
                variant="outlined"
                onClick={handleConsentNo}
                sx={{
                  borderRadius: 12,
                  px: 3,
                  fontWeight: 950,
                  textTransform: "none",
                  borderColor: "rgba(15,23,42,.35)",
                  color: "#0f172a",
                  bgcolor: "rgba(255,255,255,0.65)",
                }}
              >
                {t("no") || "No"}
              </Button>
            </Stack>
          </Box>
        );
      }

      return (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
            mb: isMobile ? 1.4 : 1.8,
          }}
        >
          {msg.sender === "bot" && (
            <Avatar
              src={botAvatar}
              sx={{
                width: isMobile ? 28 : 34,
                height: isMobile ? 28 : 34,
                mr: 1,
                bgcolor: "#e0e7ef",
                border: "1px solid rgba(148,163,184,0.28)",
              }}
              alt="Bot"
            />
          )}

          <Bubble isUser={msg.sender === "user"}>{msg.text}</Bubble>

          {msg.sender === "user" && (
            <Avatar
              src={userAvatar}
              sx={{
                width: isMobile ? 26 : 32,
                height: isMobile ? 26 : 32,
                ml: 1,
                bgcolor: "#38bdf8",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
              alt="User"
            />
          )}
        </Box>
      );
    });

  const paperSx = useMemo(() => {
    const top = "max(var(--chat-top-offset, 72px), env(safe-area-inset-top))";
    const side = 12;
    const bottomGap = 12;

    return {
      width: isMobile ? `calc(100vw - ${side * 2}px)` : 392,
      maxWidth: isMobile ? `calc(100vw - ${side * 2}px)` : 460,
      minWidth: isMobile ? `calc(100vw - ${side * 2}px)` : 330,

      ...(isMobile
        ? {
          position: "fixed",
          right: side,
          left: "auto",
          top,
          height: `calc(100dvh - ${top} - ${bottomGap}px)`,
          maxHeight: `calc(100dvh - ${top} - ${bottomGap}px)`,
          borderRadius: PANEL_RADIUS,
        }
        : {
          mt: "8vh",
          mb: "2vh",
          height: "82vh",
          maxHeight: "82vh",
          borderTopLeftRadius: 26,
          borderBottomLeftRadius: 26,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }),

      zIndex: 1400,
      background:
        "radial-gradient(1200px 500px at 10% 0%, #e0f2fe 0%, #f8fbff 42%, #f3f9fb 100%)",
      boxShadow: 22,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      border: "1px solid rgba(148,163,184,0.18)",
    };
  }, [isMobile, PANEL_RADIUS]);

  return (
    <>
      {/* Launcher */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 34, md: 42 },
          right: { xs: 14, md: 28 },
          zIndex: 1300,
        }}
      >
        <Fade in={!open}>
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              background:
                "linear-gradient(135deg, rgba(96,165,250,1) 0%, rgba(51,65,85,1) 100%)",
              color: "#fff",
              boxShadow: "0 20px 40px -24px rgba(30,41,59,.9)",
              width: isMobile ? 56 : 66,
              height: isMobile ? 56 : 66,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.14)",
              p: 0,
              "&:hover": { background: "#334155" },
            }}
            aria-label={t("open_chat") || "Open chat"}
          >
            <Avatar
              src={botAvatar}
              sx={{
                width: isMobile ? 42 : 50,
                height: isMobile ? 42 : 50,
                bgcolor: "#fff",
                objectFit: "cover",
                border: "3px solid rgba(96,165,250,1)",
              }}
            />
          </IconButton>
        </Fade>
      </Box>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        hideBackdrop
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
        }}
        sx={{
          pointerEvents: "none",
          "& .MuiDrawer-paper": { pointerEvents: "auto" },
          "& .MuiBackdrop-root": { pointerEvents: "none" },
        }}
        PaperProps={{ sx: paperSx }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              px: 2.2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: "1px solid rgba(148,163,184,0.18)",
              background: "rgba(248,251,255,0.90)",
              backdropFilter: "blur(10px)",
              position: "sticky",
              top: 0,
              zIndex: 30,
              flexShrink: 0,
              minHeight: 64,
            }}
          >
            <Avatar
              src={botAvatar}
              sx={{
                width: 60,
                height: 30,
                border: "1px solid rgba(148,163,184,0.22)",
              }}
              alt="Bot"
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={950}
                sx={{
                  color: "#0f172a",
                  lineHeight: 1.05,
                  fontSize: "1.05rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("chat_with_us") || "Chat"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(15,23,42,0.62)", fontWeight: 800 }}
              >
                {t("ai_assistant") || "AI Assistant"}
              </Typography>
            </Box>

            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.22)",
                bgcolor: "rgba(255,255,255,0.82)",
              }}
              aria-label={t("close") || "Close"}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 1.15,
              py: 1.5,
              pb: 9,
            }}
          >
            {renderMessages()}

            {thinking && (
              <Box sx={{ textAlign: "center", py: 0.9 }}>
                <Fade in={true} timeout={250}>
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    spacing={1}
                  >
                    <CircularProgress size={20} color="primary" />
                    <Typography
                      variant="body2"
                      sx={{ color: "#334155", fontWeight: 900 }}
                    >
                      {t("ai_thinking") || "Thinking..."}
                    </Typography>
                  </Stack>
                </Fade>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            component="form"
            onSubmit={handleSend} // ✅ single source of truth
            sx={{
              px: 1.05,
              pt: 0.85,
              pb: "calc(env(safe-area-inset-bottom) + 18px)",
              mx: 1,
              mb: 1.1,
              mt: 0.3,
              borderRadius: INPUT_RADIUS,
              overflow: "hidden",
              border: "1px solid rgba(148,163,184,0.18)",
              background: "rgba(248,251,255,0.92)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 18px 40px -28px rgba(15,23,42,0.35)",
              position: "sticky",
              bottom: 0,
              zIndex: 30,
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("type_message") || "Type a message..."}
                fullWidth
                size="small"
                disabled={thinking || awaitingConsent}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 999,
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputBase-input": {
                    py: 0.95,
                    px: 1.45,
                    fontSize: "0.98rem",
                  },
                }}
              />

              <IconButton
                type="submit"
                disabled={thinking || !input.trim() || awaitingConsent}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  color: "#fff",
                  background:
                    "linear-gradient(90deg, rgba(56,189,248,1) 0%, rgba(96,165,250,1) 100%)",
                  boxShadow: "0 16px 30px -22px rgba(56,189,248,.95)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, rgba(96,165,250,1) 0%, rgba(51,65,85,1) 100%)",
                  },
                  "&.Mui-disabled": { opacity: 0.5, color: "#fff" },
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
                aria-label={t("chat_send") || "Send"}
              >
                <SendIcon />
              </IconButton>
            </Stack>

            {awaitingConsent && (
              <Box sx={{ mt: 0.75 }}>
                <Divider sx={{ opacity: 0.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.65,
                    color: "rgba(15,23,42,0.65)",
                    fontWeight: 900,
                    textAlign: "center",
                  }}
                >
                  {t("consent_hint") || "Choose Yes/No above to continue."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <AppointmentForm
        open={showAppointmentForm}
        onClose={() => setShowAppointmentForm(false)}
        sessionId={getOrCreateSessionId()}
        onSuccess={() => {
          setMessages((msgs) => [
            ...msgs,
            {
              sender: "bot",
              text:
                t("appointment_success") ||
                "Your details were saved! We will contact you shortly.",
            },
            {
              sender: "bot",
              text: t("choose_service") || "Choose a service:",
              isChoice: true,
              type: "service",
              choices: buildServiceChoices(),
            },
          ]);
          setShowServices(true);
          setAwaitingConsent(false);
          setConsentShown(false);
        }}
      />
    </>
  );
}
